/*
 * File: NoMoreAbout.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:10 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 4:30 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	Typography,
	LocaleLink,
} from '../../../../../src/components';
import { buildDynamicMeta } from '../../../../../src/components/Meta';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';

// Styles
import * as S from './NoMoreAbout.styles';


/**
 * Component
 */

const NoMoreAbout = ({ meta: m }) => {

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageNoMoreAbout.metaTitle', { partnerName: partner.name }),
		description: t('pageNoMoreAbout.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': ['WebPage', 'AboutPage'],
				'@id': `${partnerDomain}/about/#WebPage`,
				name: buildDynamicMeta(meta.title, { partner }),
				description: buildDynamicMeta(meta.description, { partner }),
				url: `${partnerDomain}${locale.localePath}/about`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`,
				},
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Introduction */}
					<S.PartnerHeroSection>
						<Typography tag="p">
							<Typography className="partner-emphasis" weight="bold">The NO MORE Silence, Speak Your Truth</Typography>
							{' '}
							platform provides a safe and supportive space for people impacted by domestic or sexual violence to share their experiences, learn
							from other survivors, and connect to resources. Launched in 2023 as a collaboration between
							{' '}
							<LocaleLink
								title="NO MORE"
								aria-label="NO MORE"
								to="https://nomoredirectory.org/"
								target="_blank"
								rel="noreferrer"
							>
								NO MORE
							</LocaleLink>
							,
							{' '}
							<LocaleLink
								title="Speak Your Truth Today"
								aria-label="Speak Your Truth Today"
								to="https://www.facebook.com/groups/speak.your.truth.today"
								target="_blank"
								rel="noreferrer"
							>
								Speak Your Truth Today
							</LocaleLink>
							,
							and
							{' '}
							<LocaleLink
								title="Our Wave"
								aria-label="Our Wave"
								to="https://www.ourwave.org"
								target="_blank"
								rel="noreferrer"
							>
								Our Wave
							</LocaleLink>
							, the platform empowers survivors, raises
							awareness, and fosters a global community of support.
						</Typography>
					</S.PartnerHeroSection>

					{/* NO MORE */}
					<S.PartnerSection>
						<Typography tag="h2" weight="bold">
							NO MORE
						</Typography>
						<Typography tag="p">
							The
							{' '}
							<LocaleLink
								title="NO MORE Foundation"
								aria-label="NO MORE Foundation"
								to="https://nomoredirectory.org/"
								target="_blank"
								rel="noreferrer"
							>
								NO MORE Foundation
							</LocaleLink>
							{' '}
							(“NO MORE”) is dedicated to
							ending domestic and sexual violence by increasing awareness, inspiring action, and fueling culture change.
							With more than 1,500 allied organizations and state, local, and international chapters, NO MORE sparks
							grassroots activism, encouraging everyone—women and men, youth and adults, from all walks of life—to be
							part of the solution. The Foundation creates and provides public awareness campaigns, educational resources,
							and community organizing tools free of charge for anyone wanting to stop and prevent violence. First
							launched in 2013, NO MORE has brought together the largest coalition of advocacy groups, service providers,
							governmental agencies, major corporations, universities, communities, and individuals, all under a common
							brand and a unifying symbol supporting a world free of violence.
						</Typography>
					</S.PartnerSection>

					{/* Speak Your Truth Today */}
					<S.PartnerSection>
						<Typography tag="h2" weight="bold">
							Speak Your Truth Today
						</Typography>
						<Typography tag="p">
							<LocaleLink
								title="Speak Your Truth Today"
								aria-label="Speak Your Truth Today"
								to="https://www.facebook.com/groups/speak.your.truth.today"
								target="_blank"
								rel="noreferrer"
							>
								Speak Your Truth Today
							</LocaleLink>
							{' '}
							(SYTT)
							amplifies the voices of those impacted by domestic violence, providing education and resources that foster
							community, self-sufficiency, and overall well-being. SYTT is committed to reaching a world where a safe and
							accessible community supports everyone impacted by domestic violence to experience holistic healing. SYTT’s core
							values are education, empathy, and empowerment.
						</Typography>
					</S.PartnerSection>

					{/* Our Wave */}
					<S.PartnerSection>
						<Typography tag="h2" weight="bold">
							Our Wave
						</Typography>
						<Typography tag="p">
							Founded in 2019,
							{' '}
							<LocaleLink
								title="Our Wave"
								aria-label="Our Wave"
								to="https://www.ourwave.org"
								target="_blank"
								rel="noreferrer"
							>
								Our Wave
							</LocaleLink>
							{' '}
							is an online platform for survivors of sexual violence to share stories, receive
							customized support, and join a community of understanding. While collecting stories and messages of hope, we seek
							to empower survivors, educate society, and strategize to develop smarter prevention methods. Our Wave partners
							with violence prevention organizations worldwide to create technology that strengthens and empowers survivors.
						</Typography>
					</S.PartnerSection>

					{/* Contact Info */}
					<S.PartnerContactSection>
						<Typography tag="h4" weight="bold">
							If you have any questions, please reach out to us at
							{' '}
							<LocaleLink
								title="info@nomore.org"
								aria-label="info@nomore.org"
								to="mailto:info@nomore.org?Subject=Question"
							>
								info@nomore.org
							</LocaleLink>
							.
						</Typography>
					</S.PartnerContactSection>

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

NoMoreAbout.propTypes = {
	meta: PropTypes.shape()
};
NoMoreAbout.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default NoMoreAbout;
